import { Link } from 'react-router-dom';
import { SEO, SchemaMarkup } from '../components/seo';
import { BrochureButton } from '../components/BrochureButton';
import { Check } from 'lucide-react';

const tiers = [
  { range: 'Tot en met 10 personen', price: '€ 375', unit: 'per jaar' },
  { range: '11 t/m 250 personen', price: '€ 475', unit: 'per jaar', featured: true },
  { range: 'Per extra 250 personen', price: '+ € 475', unit: 'per jaar' },
];

const included = [
  'Externe LVV-gecertificeerde register vertrouwenspersoon',
  'Standaard onboarding',
  'PuntUit-spel, fysiek',
  'Gratis online spreekuur, eerste half uur per gesprek',
  'Periodieke awareness-mails voor bewustwording en preventie',
  'Jaarverslag / rapportage',
  'Toegang tot praatuit.nl',
  'Early Awareness Tool',
];

const extras = [
  { label: 'Vervolgbegeleiding na het spreekuur', value: '€ 84 per uur' },
  { label: 'Reistijd voor fysieke gesprekken op locatie', value: '50% van het uurtarief' },
  { label: 'Kilometervergoeding', value: '€ 0,25 per kilometer' },
  { label: 'Live onboarding en spelbegeleiding op locatie', value: 'op basis van uurtarief' },
];

const faqItems = [
  { question: 'Hoe wordt de prijs bepaald?', answer: 'Het jaarabonnement is gebaseerd op het aantal personen: tot en met 10 personen € 375 per jaar, 11 t/m 250 personen € 475 per jaar en per extra 250 personen + € 475 per jaar.' },
  { question: 'Wat als een melder na het spreekuur meer begeleiding nodig heeft?', answer: 'Het eerste half uur per gesprek is inbegrepen. Vervolgbegeleiding na het spreekuur kost € 84 per uur.' },
  { question: 'Zijn er opstartkosten?', answer: 'Nee. De standaard onboarding, het PuntUit-spel en de toegang tot praatuit.nl zijn inbegrepen in het jaarabonnement. Live onboarding en spelbegeleiding op locatie zijn optioneel, op basis van uurtarief.' },
  { question: 'Kan ik opzeggen?', answer: 'Ja. Het abonnement loopt per jaar en is jaarlijks opzegbaar.' },
];

export function Tarieven() {
  return (
    <>
      <SEO title="Tarieven | PuntUit" description="Tarieven voor PuntUit: een jaarabonnement vanaf € 375 per jaar. Externe vertrouwenspersoon, PuntUit-spel, gratis online spreekuur, zelfhulpplatform en Early Awareness Tool inbegrepen." canonicalUrl="/tarieven" />
      <SchemaMarkup
        type="service"
        pageUrl="https://puntuit.nl/tarieven"
        pageTitle="Tarieven — PuntUit"
        pageDescription="Tarieven voor PuntUit: een jaarabonnement vanaf € 375 per jaar. Alles inbegrepen."
        breadcrumb={[
          { name: 'Home', url: '/' },
          { name: 'Tarieven', url: '/tarieven' },
        ]}
      />
      <SchemaMarkup type="faqPage" faqItems={faqItems} />
      <div className="pt-28">
        <section className="bg-[#0284c7] text-white">
          <div className="container py-24 md:py-32 text-center max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Eén jaarabonnement. Alles inbegrepen.</h1>
            <p className="text-xl text-primary-100 mb-4">Een compleet programma voor bewustwording, preventie en een externe vertrouwenspersoon. Geen verrassingen, geen verborgen kosten.</p>
          </div>
        </section>

        {/* Jaarabonnement-tarieven */}
        <section className="section-padding bg-white">
          <div className="container max-w-5xl">
            <p className="text-[#0284c7] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Jaarabonnement</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10">Prijs naar aantal personen.</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {tiers.map((tier) => (
                <div
                  key={tier.range}
                  className={`rounded-2xl p-7 bg-white border ${tier.featured ? 'border-[#0284c7] shadow-md' : 'border-gray-200 shadow-sm'}`}
                >
                  {tier.featured && (
                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-[#0284c7]/10 text-[#0284c7] px-2.5 py-1 rounded-md mb-3">Meest gekozen</span>
                  )}
                  <p className="text-gray-600 text-sm mb-3">{tier.range}</p>
                  <p className="text-3xl font-bold text-gray-900">{tier.price}</p>
                  <p className="text-gray-500 text-sm">{tier.unit}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">Bedragen zijn excl. btw.</p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/contact" className="btn-primary">Plan een kennismakingsgesprek</Link>
              <BrochureButton variant="secondary" />
            </div>
          </div>
        </section>

        {/* Inbegrepen + aanvullende kosten */}
        <section className="section-padding bg-gray-50">
          <div className="container max-w-5xl grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Inbegrepen in het jaarabonnement</h2>
              <ul className="space-y-3">
                {included.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-700">
                    <Check className="w-5 h-5 text-[#0284c7] mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Aanvullende kosten</h2>
              <p className="text-gray-500 text-sm mb-6">Indien van toepassing.</p>
              <ul className="divide-y divide-gray-100">
                {extras.map((extra) => (
                  <li key={extra.label} className="flex items-baseline justify-between gap-4 py-3">
                    <span className="text-gray-700">{extra.label}</span>
                    <span className="text-gray-900 font-semibold text-right whitespace-nowrap">{extra.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section-padding bg-white">
          <div className="container max-w-4xl">
            <h2 className="text-3xl font-bold mb-8">Veelgestelde vragen over kosten</h2>
            <div className="space-y-5 text-gray-700">
              {faqItems.map((item) => (
                <p key={item.question}><strong>{item.question}</strong> {item.answer}</p>
              ))}
            </div>
            <p className="mt-8 text-gray-700">Benieuwd hoe dit tarief zich verhoudt tot het opleiden van een eigen medewerker? Lees de vergelijking tussen een <Link to="/interne-vs-externe-vp" className="underline underline-offset-2 decoration-primary-300 hover:decoration-primary-600">interne en externe vertrouwenspersoon</Link>.</p>
          </div>
        </section>

        <section className="section-padding bg-primary-700 text-white">
          <div className="container text-center max-w-3xl">
            <h2 className="text-3xl font-bold mb-4">Klaar om het goed te regelen?</h2>
            <p className="text-primary-100 mb-8">Een kennismakingsgesprek is vrijblijvend en kosteloos. Samen kijken we wat past bij jouw organisatie en cultuur.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" className="inline-block bg-white text-primary-700 px-8 py-4 rounded-xl font-semibold">Plan een kennismakingsgesprek</Link>
              <BrochureButton variant="outline-white" />
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
